// ---------------------------------------------------------------------------
// MY CLOSET (her order, 2026-08-09) — **THE WHOLE LAW OF A MEMBER'S SPIRITUAL INVENTORY.**
//
// HER WORDS: *"every member's personal spiritual inventory, so Lala and the house know what they
// own. Universal, not Conjure-exclusive."*
//
// THIS FILE IMPORTS NOTHING, which is the entry requirement every shared law in this repo has
// (R5/R8/R95): a decision inside a TSX file that holds hooks cannot be RUN by a gate, and the SERVER
// needs these same answers — the store validates a write with them and Lala's tool reads them — so a
// client-only `src/lib/*.ts` module could not hold this either. One law, both sides, no second copy.
//
// **THE CONJURE CATALOG NOW EXISTS, AND THE PARAGRAPH THAT STOOD HERE IS RETIRED AT ITS OWN
// DECLARATION RATHER THAN OVERRIDDEN BELOW IT** (DL-2/DT-2b/R181; and R68: a stated reason that no
// longer applies reads as coverage). Until ITEM 3b it said, correctly and with its receipts, that
// there was no product catalog in this database — 79 tables and not one held a product — so the
// Conjure path could only file a member's typed name for the owner's review, because **deriving a
// picker by parsing manual prose IS the machine inventing catalog entries**, which is the exact thing
// her own "not listed" clause forbids MEMBERS from doing.
//
// **ITEM 3b SUPPLIED THE REAL CATALOG SHE ALWAYS MEANT: her own export, 1,097 products, in
// `conjure_products`.** So the picker her order asked for is buildable now and is built — and the
// ruling underneath has not changed at all: **nothing is invented.** A name that resolves to a real
// catalog row is stored as that row's product; a name that resolves to NOTHING still files for her
// review and is never quietly stored as a fact (`closetConjureResolution` below is where those two
// facts stay two facts, R18).
// ---------------------------------------------------------------------------

// HER TEN TYPES, HER ORDER, HER WORDS. The roster is the law: a type not on it cannot be stored,
// because a free-typed type is a category nobody can search, group or teach from.
export const CLOSET_TYPES = [
  { key: "oil", label: "Oil" },
  { key: "candle", label: "Candle" },
  { key: "herb", label: "Herb" },
  { key: "root", label: "Root" },
  { key: "crystal", label: "Crystal" },
  { key: "seal", label: "Seal" },
  { key: "incense", label: "Incense" },
  { key: "altar-piece", label: "Altar piece" },
  { key: "tool", label: "Tool" },
  { key: "other", label: "Other" },
];

export const CLOSET_TYPE_KEYS = CLOSET_TYPES.map((t) => t.key);

// **AN UNROSTERED TYPE SHOWS ITS OWN KEY, NEVER A PLAUSIBLE LABEL** (R25's `DRAWER_ICONS` ruling,
// which this house has paid for on a moderation record and on a drawer tab). A type that fell through
// wearing "Other" would be indistinguishable from a member who really chose Other, and the one thing
// worse than a visibly missing label is a confidently wrong one.
export function closetTypeLabel(key) {
  const k = String(key ?? "").trim();
  const found = CLOSET_TYPES.find((t) => t.key === k);
  return found ? found.label : k;
}

// WHERE AN ITEM CAME FROM. Two sources and no third, because her honesty law for Lala keys on this
// exact distinction: HER OWN products she speaks on with full authority from her materials; anything
// else she reasons about from TYPE and general craft knowledge and never attributes to her teachings.
export const CLOSET_SOURCES = ["conjure", "own"];
export const CLOSET_SOURCE_LABELS = { conjure: "The Conjure", own: "Their own" };

export function closetSourceLabel(source) {
  const s = String(source ?? "").trim();
  return CLOSET_SOURCE_LABELS[s] || s;
}

// Her copy, in one place, because the SERVER composes member-facing text too (the ITEM 14b law).
export const CLOSET_TITLE = "My Closet";
export const CLOSET_SEARCH_PLACEHOLDER = "Search your closet";
export const CLOSET_ADD_LABEL = "Add";
// HER HONEST-EMPTY LINE, verbatim from her order. One warm line, and the Add button beside it.
export const CLOSET_EMPTY_LINE = "Your closet holds your spiritual items, so Lala knows what you're working with.";
// **THE `CLOSET_CONJURE_REVIEW_LINE` THAT STOOD HERE IS DELETED, NOT LEFT BESIDE ITS REPLACEMENT**
// (DL-2/DT-2b/R181). It read *"Tell us which Conjure item it is and we will add it to your closet
// once it is checked"* and it was honest while no catalog existed — every Conjure name went to her
// review. With 1,097 real products behind the box that sentence is now false for the common case:
// a tapped suggestion is added at once and nothing waits on a check. **A superseded sentence left
// standing is the thing this house has been caught enforcing seven times** (R30/R95/R116/R117/R146b/
// R148/R150), so it goes, and the two sentences that replace it are two different FACTS (R18).
export const CLOSET_CONJURE_PLACEHOLDER = "Start typing a Conjure product";
export const CLOSET_BROWSE_LABEL = "Search Conjure products";
export const CLOSET_BROWSE_TITLE = "Search Conjure products";
export const CLOSET_BROWSE_BACK = "Back to typing";
export const CLOSET_SUGGEST_SEARCHING = "Looking…";

// **HER CLAUSE 1's TWO OUTCOMES, WORDED SEPARATELY BECAUSE THEY ARE SEPARATE FACTS.** A product that
// resolved is IN her closet now; a name that resolved to nothing is ON THE OWNER'S LIST and is not in
// her closet at all. One sentence covering both would be the silent swallow her order forbids by
// name — a member told "noted" who then cannot find the thing on her own shelf.
export function closetAddedSentence(name) {
  const n = String(name ?? "").trim();
  return n ? `${n} is in your closet.` : "It is in your closet.";
}
export function closetNotListedSentence(name) {
  const n = String(name ?? "").trim();
  return n
    ? `We do not have ${n} listed yet. It has gone to Lala to check, and it will be added once she has.`
    : "That has gone to Lala to check, and it will be added once she has.";
}
export const CLOSET_NOT_LISTED_LABEL = "Not listed? Send it to be checked";
// Her "never a silent swallow" clause, said BEFORE she taps rather than only after: the box that
// matched nothing says what the control beside it will do.
export const CLOSET_NO_PRODUCT_LINE = "Nothing in the Conjure catalog matches that yet.";

export const CLOSET_NAME_MAX = 120;
export const CLOSET_NOTE_MAX = 2000;

// ---------------------------------------------------------------------------
// RAIL-9b — **THE DRAWER'S SCREENS.** Her rebuild order, 2026-08-10, after she opened it and found
// *"a bare unlabeled add-form rendered semi-transparent over the room with the rail bleeding
// through."* Her ruling: **screen one is exactly three things** — the search bar, the item list, and
// one gold Add pinned at the bottom — **and the add flow lives BEHIND Add.**
//
// The screens are a LAW rather than component state, for the reason every decision in this house
// moves out of a TSX file: a FAST gate cannot import a component that holds hooks, so a screen
// machine written inside `ClosetBody` is a screen machine nothing runs.
// ---------------------------------------------------------------------------
export const CLOSET_VIEWS = ["list", "source", "form", "detail"];

// **FIVE STATES, NOT TWO, AND THE MIDDLE ONES ARE WHY THIS IS A FUNCTION.** `null` is a read that has
// not answered and must HOLD STILL; `[]` is an ANSWER and earns her warm line; a full closet whose
// SEARCH matched nothing is a different sentence again, and telling a member with a full closet that
// it is empty is the one wrong thing to say about her own belongings (R18, and R64's cauldron
// ruling one room over). The four ternaries this replaces were scattered down the render.
export function closetListState({ items = null, failed = false, matches = 0 } = {}) {
  if (failed) return "failed";
  if (items === null) return "waiting";
  if (items.length === 0) return "empty";
  if (matches === 0) return "no-match";
  return "list";
}

// **HER "PHOTO OR TYPE ICON" CLAUSE, AS A DECISION RATHER THAN A RENDER TRICK.** A row shows her
// photograph when there is one and the item's own TYPE icon when there is not — never a generic
// glyph standing in for ten different things, which is `DRAWER_ICONS`' silent-fallback defect that
// this same round had to repair one rail over.
export function closetRowFace(item) {
  if (item && item.has_photo) return { kind: "photo", key: String(item.id) };
  const type = String(item?.type ?? "").trim();
  return { kind: "icon", key: CLOSET_TYPE_KEYS.includes(type) ? type : "unknown" };
}

// The add flow's two steps, in her order: pick where it came from, then fill the fields.
export const CLOSET_SOURCE_STEP_TITLE = "Where is it from?";
export const CLOSET_SOURCE_CHOICES = [
  { source: "conjure", label: "From The Conjure" },
  { source: "own", label: "My own item" },
];
export const CLOSET_FORM_TITLE = "Add to your closet";
export const CLOSET_SAVE_LABEL = "Save";
export const CLOSET_BACK_LABEL = "Back";
export const CLOSET_EDIT_LABEL = "Edit";
export const CLOSET_DELETE_LABEL = "Delete";
export const CLOSET_PHOTO_LABEL = "Photo (optional)";
// **HER ITEM 3 CLAUSE 2: A DESCRIPTION FIELD WITH THE PROMPT IN THE BOX.** This REPLACES
// `CLOSET_NOTE_LABEL` ("Note (optional)") at its own declaration rather than being added beside it
// (DL-2/DT-2b/R181) — it is the same field, the same column and the same 2,000-character cap, and
// two labels for one box is how a form comes to ask for two different things in one place.
//
// **THE PROMPT IS A PLACEHOLDER AND THE LABEL IS A REAL `<label>`, WHICH IS NOT A DETAIL.** Her
// words are *"the prompt IN THE BOX"*, and a placeholder disappears the moment she types — so the
// placeholder carries her sentence and the label carries the field's NAME, and a member half way
// through typing can still see what she is filling in. A prompt used AS the label is the defect
// RAIL-9b's rebuild already repaired on this same form.
export const CLOSET_DESCRIPTION_LABEL = "Description";
export const CLOSET_DESCRIPTION_PROMPT = "Describe what it is and what it's for, so Lala knows how to work with it.";
// Her *"strongly encouraged for non-Conjure items"* clause, shown as ENCOURAGEMENT and never as a
// refusal: a description is what lets Lala recommend an outside item, and a member who will not
// write one still owns the item and must still be able to save it (`closetItemViolations` is
// deliberately untouched — this is a nudge, not a gate).
export const CLOSET_DESCRIPTION_ENCOURAGE = "Lala can only work with your own items if you tell her what they are.";
export const CLOSET_NAME_LABEL = "Name";
export const CLOSET_TYPE_LABEL = "Type";
export const CLOSET_NO_MATCH_LINE = "Nothing in your closet matches that.";
export const CLOSET_WAITING_LINE = "Opening your closet…";
export const CLOSET_FAILED_LINE = "Your closet could not be read just now. Try again in a moment.";

// **HER SOURCE CHOICES ARE DERIVED FROM `CLOSET_SOURCES`, HELD BOTH WAYS.** Two hand-typed lists is
// how a picker comes to offer what the store refuses (R15), and this picker's two options are the
// exact values the validator checks.
export function closetSourceChoicesValid() {
  const offered = CLOSET_SOURCE_CHOICES.map((c) => c.source);
  return offered.length === CLOSET_SOURCES.length && CLOSET_SOURCES.every((s) => offered.includes(s));
}

// WHAT AN EDIT SENDS. Only the fields her form can change, and `undefined` is never sent — a PATCH
// carrying `note: undefined` serialises to a body with no note, which the store reads as "leave it"
// rather than "clear it", so an intentional clear must travel as an empty STRING.
export function closetEditPatch({ name = "", type = "", note = "" } = {}) {
  return { name: String(name).trim(), type: String(type).trim(), note: String(note ?? "") };
}

// ---------------------------------------------------------------------------
// ITEM 3 CLAUSE 1 — **THE SMART PATH: WHAT THE BOX SHOWS UNDER ITSELF AS SHE TYPES.**
//
// Address-autocomplete shaped, which is her own comparison. **FIVE STATES, NEVER TWO** (R18), and
// every one of the middle three is a different fact about the same box:
//
//   `idle`      — she has typed less than the minimum; there is no question to answer yet.
//   `searching` — a request is out. **This must not read as "nothing matches"**, which is the exact
//                 collapse `closetListState` exists to prevent one screen up: telling a member her
//                 product does not exist because the network was slow is the one wrong thing to say.
//   `none`      — the catalog ANSWERED and holds nothing like it. This is the state her "not listed"
//                 clause hangs off, and it is the ONLY state that may offer the review control.
//   `results`   — tap one and it is added.
//   `failed`    — the read did not come back at all. Different from `none` for the same reason
//                 `searching` is: an unreachable catalog has not said a product is missing.
//
// **`none` AND `failed` ARE SPLIT DELIBERATELY, AND THE SPLIT IS WHAT PROTECTS HER REVIEW LIST.**
// Collapsing them would file every product a member typed during an outage onto the owner's desk as
// a missing product, which is a queue of false work with no symptom (R19's silent direction).
export const CLOSET_SUGGEST_MIN = 2;

export function closetSuggestState({ typed = "", results = null, searching = false, failed = false } = {}) {
  if (String(typed).trim().length < CLOSET_SUGGEST_MIN) return "idle";
  if (failed) return "failed";
  if (searching) return "searching";
  if (results === null) return "searching";
  return results.length ? "results" : "none";
}

/** Her "not listed" control shows in exactly one state, and it is asked rather than inferred. */
export function closetMayOfferReview(state) {
  return state === "none";
}

// ---------------------------------------------------------------------------
// **WHAT TYPE A CATALOG PRODUCT IS, IN HER TEN.** Her export has no type column — the KIND is the
// leading pipe segment (`ETHER Conjure Oil | Halo`) — so a tapped suggestion has to arrive at one of
// `CLOSET_TYPES` before the store will hold it, and the roster is HERS and closed.
//
// **IT LIVES HERE RATHER THAN IN `src/conjureCatalog.js` BECAUSE IT MAPS *INTO* THIS ROSTER**, and
// the roster is what must never be second-guessed: a mapper written next to the catalog would be a
// second opinion about what a closet type is, and the two would drift on the first type she adds
// (R28/R29). Both modules still import nothing, so a FAST gate RUNS this.
//
// **WHOLE WORDS, NEVER SUBSTRINGS — the warning her own queue paid for before a line was written.**
// A substring rule reads `Capricorn` as a cap and `Esteem` as a tee. Every entry below is matched as
// a word against the kind's own tokens.
//
// **AND AN UNMAPPED KIND ANSWERS `other`, WHICH IS HER OWN ROSTERED TYPE AND NOT A GUESS.** This is
// the one place `DRAWER_ICONS`' ruling reads differently and the difference is worth writing down:
// there, an unrostered key fell through to a *plausible wrong* icon, which is indistinguishable from
// a correct one. Here `other` claims nothing — it is exactly what the house knows, it is a type a
// member picks for herself every day, and the alternative is REFUSING to add a product she owns
// because the house could not categorise it, which fails her clause 1 outright.
const CONJURE_KIND_TYPES = [
  ["oil", ["oil", "oils", "parfum", "perfume", "cologne", "anointing"]],
  ["candle", ["candle", "candles", "vigil", "novena", "taper", "votive"]],
  ["herb", ["herb", "herbs", "botanical", "botanicals", "leaf", "leaves", "flower", "flowers"]],
  ["root", ["root", "roots", "bark", "chip", "chips"]],
  ["crystal", ["crystal", "crystals", "stone", "stones", "gem", "gems", "quartz", "pendant", "pendants", "bracelet", "bracelets", "necklace", "necklaces"]],
  ["seal", ["seal", "seals", "pentacle", "pentacles", "talisman", "amulet", "plate", "plates", "tag", "tags"]],
  ["incense", ["incense", "resin", "resins", "smoke", "charcoal", "censer", "sage", "smudge"]],
  ["altar-piece", ["altar", "shrine", "statue", "statues", "icon", "chalice", "offering"]],
  ["tool", ["tool", "tools", "kit", "kits", "cauldron", "censer", "burner", "mortar", "wand", "blade"]],
];

export function closetTypeForConjureKind(kind) {
  const tokens = closetSearchKey(kind).split(/[^a-z0-9]+/).filter(Boolean);
  if (!tokens.length) return "other";
  for (const [type, roster] of CONJURE_KIND_TYPES) {
    if (tokens.some((t) => roster.includes(t))) return type;
  }
  return "other";
}

/**
 * **ONE CATALOG PRODUCT -> THE EXACT BODY THE ADD ROUTE IS SENT.** Composed here so the client and
 * the SERVER cannot disagree about what tapping a suggestion means: the route re-resolves the name
 * against the catalog and re-derives the type through this same function, so a client that sent
 * something else is corrected rather than believed.
 *
 * **THE CATALOG'S DESCRIPTION IS NOT COPIED INTO HER NOTE, AND THAT IS THE DECISION.** Her clause 2
 * asks for a description on OWN items, *"because it is what lets Lala recommend it"* — a Conjure
 * product needs no such thing, because Lala has the manual and the catalog row itself. Copying it in
 * would freeze a snapshot of the copy into every member's closet, so her next export would leave
 * 1,300 stale descriptions nobody can find (R28/R29). The row names the product; the catalog says
 * what it is.
 */
export function closetItemFromProduct(product) {
  const name = String(product?.name ?? "").trim();
  return {
    name,
    type: closetTypeForConjureKind(product?.kind),
    source: "conjure",
    note: "",
  };
}

/**
 * **THE SERVER'S SIDE OF HER CLAUSE 1, AND THE ONE LAW THAT DECIDES WHETHER A ROW IS WRITTEN.**
 *
 * A member sending `source: "conjure"` is asserting that this is one of HER products. The house does
 * not take that on the client's word — not because a member would lie, but because the whole point of
 * the catalog is that *what exists* has ONE answer, and a closet full of typed near-misses is a
 * closet Lala reads as her own products and speaks on with the house's authority. **That is the Halo
 * defect running backwards**: instead of denying something real, she would confirm something that
 * was never made.
 *
 * Three outcomes, and the middle one is the round (R18):
 *   `add`        — the name resolved to a real catalog row. Store the CATALOG'S OWN name, so the
 *                  shelf spells her product the way she spells it and Lala can resolve it later.
 *   `not-listed` — nothing matched. **Her review, and the member TOLD.** Never stored as a fact.
 *   `own`        — not a Conjure claim at all; this law has no opinion and the ordinary path runs.
 *
 * `resolve` is a PARAMETER rather than a database read, so a gate DRIVES this over catalogs this
 * repo does not contain (R129/R135) and the route stays the only thing that touches a pool.
 */
export function closetConjureResolution(item, resolve) {
  const source = String(item?.source ?? "").trim();
  if (source !== "conjure") return { outcome: "own", item };
  const typed = String(item?.name ?? "").trim();
  const found = typed ? (typeof resolve === "function" ? resolve(typed) : null) : null;
  if (!found) return { outcome: "not-listed", typed, message: closetNotListedSentence(typed) };
  return {
    outcome: "add",
    product: found,
    // The member's own note survives if she wrote one; the type is the house's derivation and is
    // never read off the request, or the client decides what her product IS.
    item: { ...closetItemFromProduct(found), note: String(item?.note ?? "") },
    message: closetAddedSentence(found.name),
  };
}

// ---------------------------------------------------------------------------
// WHAT A CLOSET ITEM MUST BE. Returned as a LIST OF VIOLATIONS rather than a boolean, because the
// route has to tell her WHICH field was refused and a boolean cannot (R18: two facts, one answer).
// The store calls this before it writes, so a client that skips the form still cannot store a
// nameless item or an unrostered type.
// ---------------------------------------------------------------------------
export function closetItemViolations(item) {
  const out = [];
  const name = String(item?.name ?? "").trim();
  if (!name) out.push("name-empty");
  if (name.length > CLOSET_NAME_MAX) out.push("name-long");

  const type = String(item?.type ?? "").trim();
  if (!type) out.push("type-empty");
  else if (!CLOSET_TYPE_KEYS.includes(type)) out.push("type-unrostered");

  const source = String(item?.source ?? "").trim();
  if (!source) out.push("source-empty");
  else if (!CLOSET_SOURCES.includes(source)) out.push("source-unrostered");

  if (String(item?.note ?? "").length > CLOSET_NOTE_MAX) out.push("note-long");
  return out;
}

// =============================================================================================
// >>> **BULK ADD — HER SITE-WIDE LIST, ITEM 5 (2026-08-28).** <<<
//
// **WHAT IT IS FOR, MEASURED OFF HER OWN SCREEN:** a member who owns six of her Conjure products
// adds them one at a time, and the shelf does not stay open between them. `addProduct` awaits the
// reload and then calls `toList()` — her own clause, *tap one, it is added, and it APPEARS
// IMMEDIATELY in the closet list* — so every item costs open-browse, open-shelf, tap, and land back
// on the list. Six items is that dance six times.
//
// **HER TAP CLAUSE IS NOT TOUCHED, AND THAT IS DELIBERATE.** Tapping a product still adds it and
// still lands her on the list holding it. Bulk is a MODE she turns on, so the behaviour she
// specified is what an ordinary tap still does (R141: a shipped clause is not quietly re-decided
// because a later round found a second use for the same tap).
// =============================================================================================

/**
 * **THE CAP, AND IT IS A REAL NUMBER RATHER THAN A GESTURE.** Every selected item is a resolve
 * against her catalog and a row written, so an unbounded selection is an unbounded write from one
 * tap. Her largest shelf decides whether this bites: the cap is above any plausible honest
 * selection and far below a shelf-select-all, which is the direction that keeps a member's real
 * intent working while refusing the one that empties a catalog into a closet.
 */
export const CLOSET_BULK_MAX = 25;

export const CLOSET_BULK_ENTER = "Select several";
export const CLOSET_BULK_LEAVE = "Done selecting";
export const CLOSET_BULK_NONE = "Tap the ones you own.";

/** The button's own words, which carry the COUNT because a button that says *Add* while five are
 *  ticked is a button she has to count for herself. */
export function closetBulkAddLabel(n) {
  const count = Number(n) || 0;
  if (count <= 0) return "Add to my closet";
  return count === 1 ? "Add 1 to my closet" : `Add ${count} to my closet`;
}

/**
 * **WHAT REFUSES A SELECTION BEFORE A SINGLE ROW IS WRITTEN.** Two facts and not one (R18): an
 * EMPTY selection is a member who has not chosen yet, and an OVERSIZED one is a request this house
 * will not carry out. Collapsing them would tell a member who ticked nothing that she ticked too
 * much.
 */
export function closetBulkViolations(names) {
  const list = Array.isArray(names) ? names : [];
  const out = [];
  if (!list.length) out.push("bulk-empty");
  if (list.length > CLOSET_BULK_MAX) out.push("bulk-too-many");
  return out;
}

/**
 * >>> **HER SENTENCE AFTER A BULK ADD, AND IT NAMES EVERY OUTCOME THAT HAPPENED.** <<<
 *
 * **A BULK ADD IS PARTIAL BY NATURE AND THE SENTENCE MUST SAY SO (R18/R19).** Some names resolve to
 * her catalog and are stored; some resolve to nothing and are NOT stored; some are refused by the
 * item law. *"Added"* alone over a run where two silently failed is the one sentence that costs a
 * member her trust in the screen — she looks at her closet later and two are missing, with nothing
 * anywhere having said so.
 *
 * **AND THE NOT-LISTED HALF IS NOT FILED FOR REVIEW HERE (her `fileForReview` law, kept).** That
 * path is reachable from exactly one state and by the member's own decision; a bulk run that posted
 * twenty unknown names onto the owner's desk would be this house deciding for her.
 */
export function closetBulkSummary(results) {
  const rows = Array.isArray(results) ? results : [];
  const added = rows.filter((r) => r && r.outcome === "added").length;
  const missing = rows.filter((r) => r && r.outcome === "not-listed");
  const refused = rows.filter((r) => r && r.outcome === "refused").length;
  if (!rows.length) return "";

  const parts = [];
  if (added > 0) parts.push(added === 1 ? "1 added to your closet." : `${added} added to your closet.`);
  // NAMED, not counted: *one was not found* sends her hunting through a shelf to work out which.
  if (missing.length === 1) parts.push(`${missing[0].name} is not in the Conjure catalog yet, so it was not added.`);
  else if (missing.length > 1) {
    parts.push(`${missing.map((r) => r.name).join(", ")} are not in the Conjure catalog yet, so they were not added.`);
  }
  if (refused > 0) parts.push(refused === 1 ? "1 could not be saved." : `${refused} could not be saved.`);
  // Nothing at all happened, which is its own answer rather than an empty string that reads as
  // success (R43's M8 on her own screen).
  if (!parts.length) return "Nothing was added.";
  return parts.join(" ");
}

export function closetItemValid(item) {
  return closetItemViolations(item).length === 0;
}

// ---------------------------------------------------------------------------
// THE SEARCH. Hers is *"a search bar at top"* over her own shelf, so it is a filter over rows this
// member already owns and never a query that could reach anybody else's.
//
// **IT SEARCHES THE NAME, THE TYPE AND THE NOTE.** A member who wrote "for court" in the note of an
// oil she named "Fiery Wall" is searching for the note when she types "court", and a search that read
// only the name would answer "nothing" about something she is looking straight at.
//
// The match is case-folded and accent-blind so a member typing `florida` finds `Flórida`.
// ---------------------------------------------------------------------------
export function closetSearchKey(value) {
  return String(value ?? "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .trim();
}

export function closetSearch(items, term) {
  const list = Array.isArray(items) ? items : [];
  const q = closetSearchKey(term);
  // AN EMPTY SEARCH IS NOT A FILTER. It returns the whole shelf, because the box is empty on arrival
  // and a member who has typed nothing has not asked a question.
  if (!q) return list;
  return list.filter((it) => {
    const hay = [it?.name, closetTypeLabel(it?.type), it?.type, it?.note]
      .map((x) => closetSearchKey(x))
      .join(" ");
    return hay.includes(q);
  });
}

// HER ORDER: *"newest first"*. Held here rather than in the SQL alone so the client cannot re-sort it
// into a different answer, and so a gate can RUN the ordering without a database.
export function closetNewestFirst(items) {
  const list = Array.isArray(items) ? [...items] : [];
  return list.sort((a, b) => {
    const at = Date.parse(a?.createdAt ?? "") || 0;
    const bt = Date.parse(b?.createdAt ?? "") || 0;
    if (bt !== at) return bt - at;
    // A STABLE TIE-BREAK, because two items added in the same second must not swap places between
    // renders — a list that reorders under her thumb reads as items appearing and vanishing.
    return String(b?.id ?? "").localeCompare(String(a?.id ?? ""));
  });
}

// ---------------------------------------------------------------------------
// LALA'S HONESTY LAW, AS A FUNCTION RATHER THAN A PARAGRAPH OF PROMPT.
//
// HER ORDER: her OWN products she speaks on with full authority from her materials; outside items she
// reasons from TYPE and general craft knowledge, **never inventing what another maker's product
// does, never attributing outside products to her teachings.**
//
// This returns the DIRECTIVE the tool result carries, per item, so the instruction rides beside the
// data it is about instead of sitting in one system-prompt sentence that a long turn can drift from.
// The attribution and fabrication gates still run on her prose; this is what makes them agree.
// ---------------------------------------------------------------------------
export function closetAuthority(source) {
  return String(source ?? "").trim() === "conjure" ? "house" : "outside";
}

export function closetItemDirective(item) {
  const name = String(item?.name ?? "").trim() || "this item";
  if (closetAuthority(item?.source) === "house") {
    return `${name} is one of the house's own items. Speak on it with full authority from your own materials.`;
  }
  const type = closetTypeLabel(item?.type) || "item";
  // **THE "DO NOT GO LOOKING FOR IT IN YOUR OWN CATALOG" CLAUSE WAS ADDED AFTER A REAL TURN, AND IT
  // IS A COLLISION BETWEEN TWO CORRECT LAWS (ITEM 3 clause 4, measured 2026-08-11).** The catalog
  // round's third state says an unrostered name is `UNCONFIRMED`, never *"not mine"* — right, and
  // load-bearing, for a product a member NAMES in conversation with no provenance at all. **It is
  // wrong for a closet row**, where the member has already told the house where the thing came from.
  // Measured on a real turn: asked about `Anaya's Rose Candle`, marked `own`, Lala answered *"the
  // name did not come back confirmed… I would rather check than tell you something off about ONE OF
  // MY OWN CANDLES"* and reasoned from *"rose across my entire line"* — **the house claiming a
  // stranger's candle**, which is the same fabrication class as denying a real one, pointing the
  // other way. The provenance is a FACT the member supplied, so the directive states it as one.
  return `${name} is the member's own ${String(type).toLowerCase()}, from outside this house — the member told us so when they added it, so this is settled and you do NOT need to check whether it is one of yours. It is not. Do not look for it in your own catalog, do not call it one of yours, and do not reason about it from your own product line. Reason ONLY from what that TYPE of item does in general craft knowledge. Never state what this particular maker's product does, and never attribute it to your teachings.`;
}

// WHAT SHE SAYS WHEN THE SHELF CANNOT SERVE THE ASK. Her order names this outcome explicitly, so it
// is a rostered answer rather than something she improvises: *"if nothing on the shelf serves, she
// says so honestly and teaches from the house."*
export const CLOSET_NO_MATCH_DIRECTIVE = "Nothing in this member's closet genuinely serves what they asked for. Say so plainly and warmly, then teach them from the house's own way. Do not stretch an item on their shelf to fit.";

// ---------------------------------------------------------------------------
// THE CAULDRON'S READING OF THE CLOSET (her clause 6).
//
// **THE CLOSET INFORMS MATERIAL SELECTION AND NEVER ALTERS HER METHOD**, which is her sentence and
// is the whole risk in this clause. So this returns the OWNED items that match materials her method
// already named — it can never ADD a material, only mark one she already called for as owned.
// A working that named nothing this member owns comes back empty and the method is untouched.
// ---------------------------------------------------------------------------
// **THE MATCH IS TOKEN SUBSET, NOT SUBSTRING, AND THAT WAS CAUGHT BY RUNNING IT RATHER THAN BY
// READING IT.** The first draft asked whether either name CONTAINED the other, which reads as
// obviously right and fails her actual case: her method says `Fiery Wall of Protection oil` and the
// shelf says `Fiery Wall oil`, and neither string contains the other because the words interleave.
// Every real pairing in this house has that shape — a member's shorthand against her full name.
//
// So the rule is: one name's words are all present in the other's. **AND IT TAKES AT LEAST TWO
// SHARED WORDS**, because a shelf item called simply `Oil` is a subset of every oil she ever names,
// and a Cauldron that told a member she owns the material for every working would be worse than one
// that told her nothing.
function nameTokens(value) {
  return closetSearchKey(value).split(/[^a-z0-9]+/).filter(Boolean);
}

export function closetOwnedFor(materials, items) {
  const owned = Array.isArray(items) ? items : [];
  const wanted = (Array.isArray(materials) ? materials : [])
    .map((m) => nameTokens(m))
    .filter((t) => t.length);
  if (!wanted.length || !owned.length) return [];
  const out = [];
  for (const it of owned) {
    const mine = nameTokens(it?.name);
    if (!mine.length) continue;
    const hit = wanted.some((w) => {
      const short = mine.length <= w.length ? mine : w;
      const long = mine.length <= w.length ? w : mine;
      if (short.length < 2) return short.length === 1 && long.length === 1 && short[0] === long[0];
      return short.every((tok) => long.includes(tok));
    });
    if (hit && !out.some((o) => String(o.id) === String(it.id))) out.push(it);
  }
  return out;
}

// The sentence her order quotes, composed once so the Cauldron and Lala cannot word it differently.
export function closetOwnedSentence(item, { working = "this working" } = {}) {
  const name = String(item?.name ?? "").trim();
  if (!name) return "";
  return `You have ${name} on your shelf, ${working} uses it.`;
}

// ---------------------------------------------------------------------------
// CLOSET-1 (her order, 2026-08-14) — **SEND-TO-CAULDRON FROM ANY CLOSET ITEM, MERCHANDISE REFUSED
// WARMLY.**
//
// >>> **THE DECISION IS THE DOOR'S, BECAUSE ONLY THE DOOR CAN SEE HER CATALOG.** <<< A closet row
// carries a TYPE and a SOURCE and no product class, so the class has to be looked up — and a client
// that classified her item itself would be a second answer to *what merchandise is* (R28/R29) on the
// one question ITEM 3b spent a whole round getting right. The law below takes the class as a
// PARAMETER (R46's locked-door shape), so a `.mjs` gate RUNS every outcome with no database.
//
// >>> **AND ONLY A POSITIVE MERCHANDISE FINDING REFUSES. EVERYTHING ELSE SENDS.** <<< That asymmetry
// is the round's one real decision and it is R19's direction:
//   * refusing wrongly costs a member the use of something she OWNS and typed into her own
//     inventory, on the surface whose whole purpose is that Lala knows what she is working with;
//   * sending wrongly needs her to have really put a t-shirt in her closet AND the classifier to
//     have missed it AND her to have chosen to send it — and the pot's own gate stack is downstream.
// So `ambiguous` SENDS, an unreadable class SENDS, and an unrostered class SENDS. ITEM 3b's own
// ruling (*"it fails to ambiguous, never to practice"*) is about what goes on HER REVIEW LIST, which
// is a different question from what a member may do with her own shelf.
// **AND THERE IS NO LABEL HERE, WHICH IS A DELETION RATHER THAN AN OMISSION (R28/R29/R165).** The
// first draft declared `CLOSET_SEND_LABEL` and nothing ever read it: the SHARED sender already says
// `cauldron.send`, in both languages, on eleven other surfaces. A second spelling of that button's
// words would be a Spanish member reading one English control inside an otherwise translated send —
// and a constant with no caller reads as a capability this house has and does not.
export const CLOSET_SEND_VERDICTS = { send: "send", merchandise: "refused-merchandise" };

/**
 * PURE. `productClass` is `"practice" | "merchandise" | "ambiguous"` or anything at all — including
 * `null` for an item this house has no catalog row for, which is every item she added herself.
 */
export function closetSendVerdict({ productClass = null } = {}) {
  return String(productClass ?? "") === "merchandise"
    ? CLOSET_SEND_VERDICTS.merchandise
    : CLOSET_SEND_VERDICTS.send;
}

// **WARM, AND IT NAMES WHAT THE THING IS RATHER THAN WHAT SHE DID WRONG.** She has not made a
// mistake — she owns it, it is hers, and it simply is not a working material.
export const CLOSET_MERCH_REFUSAL =
  "That one is not a working piece, love — it is yours to wear and keep rather than to burn or dress. Send me an oil, a candle, a herb, a root or a seal off your shelf and I will put it in the pot.";

/**
 * **WHAT RIDES TO THE POT, AND IT IS BUILT FROM HER OWN ROW RATHER THAN FROM A REQUEST.** The name,
 * the type and her own note — no photo, no id and nothing the compiler could mistake for a
 * generated working. Her note rides VERBATIM, because it is her words about her own material
 * (MR-NP's ruling for the notepaper, one kind over).
 */
export function closetCauldronPayload(item) {
  const name = String(item?.name ?? "").trim();
  if (!name) return null;
  const type = String(item?.type ?? "").trim();
  return {
    material: { name, type, typeLabel: closetTypeLabel(type), source: String(item?.source ?? "").trim(), note: String(item?.note ?? "").trim() },
  };
}

// ---------------------------------------------------------------------------
// C3 — **A REFUSED ADD LEAVES A ROW, BECAUSE HER REPORT COULD NOT BE ANSWERED WITHOUT ONE.**
//
// Her surfaced ticket (#12, `selikaunderwood@hotmail.com`, 2026-08-10, and she said it AGAIN on
// 08-13): *"added items to their closet but the closet is showing empty every time."* Traced against
// the live database: **the closet read and write are not broken** (6,308 rows across 138 members,
// the newest hours old), **her account really does hold zero rows**, and she has a live grant, a
// profile and a room — so nothing about her seat explains it.
//
// **AND WHY HER ADDS DID NOT LAND IS UNANSWERABLE, BECAUSE EVERY REFUSAL ON THIS PATH ANSWERS
// `refuse(response, 200, ...)` AND RECORDS NOTHING ANYWHERE.** `gate_log` holds ZERO rows naming the
// closet, all time. That is D-1/D-4's finding on a third door and C1's on a fourth: *the trace could
// not name a cause because the cause was never written down.* One member reported it twice, and the
// only instrument this house had was asking her to try again.
//
// **THE ROW CARRIES NO MEMBER-TYPED TEXT** (P2-4C binds on anything durable, and `gate_log` is
// forever). The item's NAME and NOTE are hers and never stored here; what is stored is the outcome,
// the rostered source and type, the violation keys the validator already named, and the two LENGTHS
// — numbers, which is what tells a 200-character name apart from an empty one without keeping a word
// of it.
//
// **AN UNROSTERED OUTCOME RECORDS NOTHING**, so a caller inventing a name for a refusal cannot put an
// unaccountable row on a table nobody can edit — and a `source` or `type` this house does not roster
// is written as `other`, never as the string the member sent (that string is member-typed too).
// ---------------------------------------------------------------------------

export const CLOSET_ADD_GATE = "closet-add-refused";

/** Every way the add door can say no, named. A refusal that is not one of these is not recorded. */
export const CLOSET_REFUSAL_OUTCOMES = ["signed-out", "no-room", "not-listed", "shape", "photo", "store"];

const rosteredOr = (value, roster) => (roster.includes(String(value ?? "").trim()) ? String(value).trim() : "other");
const lengthOf = (value) => (typeof value === "string" ? value.trim().length : 0);

/**
 * The durable account of one refused add, or `null` when there is nothing accountable to write.
 *
 * It is a pure law rather than an object literal at the call site for the reason every receipt in
 * this house is: the no-member-text rule is a PROPERTY a gate can RUN, not a promise in a comment.
 */
export function closetAddReceipt(outcome, { userId = null, item = {}, violations = [] } = {}) {
  const kind = String(outcome ?? "").trim();
  if (!CLOSET_REFUSAL_OUTCOMES.includes(kind)) return null;
  const row = item && typeof item === "object" ? item : {};
  return {
    outcome: kind,
    // **PRESENCE, NEVER COERCION** (R53/R136/R137): `Number(true)` is 1 and `Number("7")` is 7, and
    // member #1 and member #7 are people. A row filed against the wrong member is worse than a row
    // filed against nobody, because only one of the two can be read as evidence about somebody.
    userId: typeof userId === "number" && Number.isSafeInteger(userId) && userId > 0 ? userId : null,
    source: rosteredOr(row.source, CLOSET_SOURCES),
    type: rosteredOr(row.type, CLOSET_TYPE_KEYS),
    violations: Array.isArray(violations) ? violations.filter((v) => typeof v === "string").slice(0, 8) : [],
    nameLength: lengthOf(row.name),
    noteLength: lengthOf(row.note),
  };
}
// ---------------------------------------------------------------------------
// IT #121 — **THE ADD PATH HAS A RECEIPT AND THE READ PATH HAD NONE, SO "MY CLOSET IS EMPTY" WAS
// UNANSWERABLE AFTER THE FACT.**
//
// **TRACED READ-ONLY BEFORE A LINE WAS WRITTEN (R25/R180), AND HER TICKET'S OWN PREMISE DOES NOT
// HOLD.** Na'ilah Janai Campbell (`divin3inn3rgsourc3@gmail.com`, #152873) reported that none of her
// inventory shows in her closet view. Measured against the live table: **105 rows, every class her
// ticket names** — 26 alchemy plates, 10 altar pieces, 9 pendants, 6 ori scarves, 3 crushed conjure,
// 4 mojo bags, 9 roots, 3 parfums — written in one 30-minute session on 2026-08-11. Her account is
// one row with a profile, a birth date and an active Vet subscription; there is no second account.
// **And the read path returns all 105 of them correctly today: `listCloset` answers 105 items in
// 158ms over 21KB with zero malformed rows, the route hands them over whole, and the client tells a
// FAILED read apart from an empty shelf.** So nothing here is a lost row and nothing is a wrong
// render, and no cause was invented for her (R19/R141).
//
// **>>> WHAT IS REALLY MISSING IS THE INSTRUMENT, AND IT IS THE SAME ABSENCE D-1/D-4, MONEY-1, C1
// AND C3 ITSELF EACH HAD TO REPAIR BEFORE A CAUSE COULD BE NAMED. <<<** C3 built the ADD receipt at
// six call sites precisely so a member saying *my adds do not land* is answerable in one query. **A
// READ that refuses or THROWS left no row anywhere** — and a thrown read is not hypothetical here:
// PERF-1 measured this house's five-connection pool saturating on exactly this class of surface, and
// `handleList` had no catch at all, so a read that died became a generic 500, the drawer drew its
// failed face, and which member and why were recorded NOWHERE.
//
// **THE READ RECEIPT IS ITS OWN GATE NAME AND ITS OWN OUTCOME ROSTER (R18).** *She was not signed
// in*, *her room was not awake* and *the store could not answer* are three different facts about a
// closet that did not populate, and only the third is an outage. Folding them into the add gate
// would put two different questions under one name on the one table an owner reads to answer this.
// ---------------------------------------------------------------------------

export const CLOSET_READ_GATE = "closet-read-refused";

/** Every way the list door can fail to hand over her shelf, named. Anything else records nothing. */
export const CLOSET_READ_OUTCOMES = ["signed-out", "no-room", "store"];

/**
 * The durable account of one closet read that did not deliver, or `null` when there is nothing
 * accountable to write.
 *
 * **IT KEEPS NOT ONE WORD SHE TYPED** (P2-4C binds on anything durable, and `gate_log` has a writer,
 * a reader and no editor): the outcome, her member id, and — where the store threw — the error's own
 * first line, bounded. Her item names, her notes and her photographs are hers and are never here.
 */
export function closetReadReceipt(outcome, { userId = null, error = "" } = {}) {
  const kind = String(outcome ?? "").trim();
  if (!CLOSET_READ_OUTCOMES.includes(kind)) return null;
  return {
    outcome: kind,
    // PRESENCE, NEVER COERCION (R53/R136/R137), for `closetAddReceipt`'s own reason: a row filed
    // against the WRONG member is worse than one filed against nobody.
    userId: typeof userId === "number" && Number.isSafeInteger(userId) && userId > 0 ? userId : null,
    // The reason is the machine's own and never a member's words. Bounded, because an unbounded
    // vendor string on a permanent row is the shape D-4 already refused.
    reason: typeof error === "string" ? error.replace(/\s+/g, " ").trim().slice(0, 200) : "",
  };
}

